import { staticUtils } from './Data/staticUtils'

const breakpoints: Record<string, number> = {
	sm: 640,
	md: 768,
	lg: 1024,
	xl: 1280
}

const spacingProps: Record<string, string[]> = {
	p: ['padding'],
	pt: ['padding-top'],
	pb: ['padding-bottom'],
	pl: ['padding-left'],
	pr: ['padding-right'],
	px: ['padding-left', 'padding-right'],
	py: ['padding-top', 'padding-bottom'],
	m: ['margin'],
	mt: ['margin-top'],
	mb: ['margin-bottom'],
	ml: ['margin-left'],
	mr: ['margin-right'],
	mx: ['margin-left', 'margin-right'],
	my: ['margin-top', 'margin-bottom'],
	gap: ['gap']
}

const negativeSpacingPrefixes = new Set([
	'm',
	'mt',
	'mb',
	'ml',
	'mr',
	'mx',
	'my'
])

const sizingProps: Record<string, string> = {
	w: 'width',
	h: 'height',
	'min-w': 'min-width',
	'max-w': 'max-width',
	'min-h': 'min-height',
	'max-h': 'max-height'
}

const positionProps: Record<string, string> = {
	top: 'top',
	bottom: 'bottom',
	left: 'left',
	right: 'right',
	inset: 'inset'
}

const parseValue = (val: string): string | null => {
	if (val === 'full') return '100%'
	if (val === 'auto') return 'auto'
	if (val === 'screen') return '100vh'
	const num = Number.parseInt(val, 10)
	if (!Number.isNaN(num)) return `${num * 0.25}rem`
	return null
}

const parseSpacingValue = (val: string): string | null => {
	if (val === 'full') return '100%'
	if (val === 'auto') return 'auto'
	const num = Number.parseInt(val, 10)
	if (Number.isNaN(num)) return null
	if (num === 0) return '0'
	return `calc(var(--spacing-unit, 0.25rem) * ${num})`
}

const parseSignedValue = (negative: boolean, val: string) => {
	const value = parseValue(val)
	if (!value) return null
	if (!negative || value === 'auto') return value
	return `-${value}`
}

const parseSignedSpacingValue = (negative: boolean, val: string) => {
	const value = parseSpacingValue(val)
	if (!value) return null
	if (!negative || value === 'auto' || value === '0') return value
	if (value.startsWith('calc(')) return `calc(-1 * ${value})`
	return `-${value}`
}

export const generateRule = (name: string): string | null => {
	const responsiveMatch = name.match(/^(sm|md|lg|xl):(.+)$/)
	if (responsiveMatch) {
		const [, prefix, innerName] = responsiveMatch
		const innerRule = generateRule(innerName)
		if (!innerRule) return null
		const minWidth = breakpoints[prefix]
		const escapedName = name.replace(/:/g, '\\:')
		const innerBodyMatch = innerRule.match(/^\.[^{]+\{(.+)\}$/)
		if (!innerBodyMatch) return null
		return `@media (min-width: ${minWidth}px){.${escapedName}{${innerBodyMatch[1]}}}`
	}

	if (staticUtils[name]) {
		return `.${name}{${staticUtils[name]}}`
	}

	for (const [prefix, props] of Object.entries(spacingProps)) {
		const match = name.match(new RegExp(`^(-)?${prefix}-(\\d+|full|auto)$`))
		if (match) {
			if (match[1] === '-' && !negativeSpacingPrefixes.has(prefix)) {
				return null
			}

			const value = parseSignedSpacingValue(match[1] === '-', match[2])
			if (!value) return null
			const rules = props.map((p) => `${p}:${value}`).join(';')
			return `.${name}{${rules}}`
		}
	}

	for (const [prefix, prop] of Object.entries(sizingProps)) {
		const match = name.match(
			new RegExp(`^${prefix}-(\\d+|full|auto|screen)$`)
		)
		if (match) {
			const value = parseValue(match[1])
			if (!value) return null
			return `.${name}{${prop}:${value}}`
		}
	}

	const size = name.match(/^size-(\d+|full|auto|screen)$/)
	if (size) {
		const value = parseValue(size[1])
		if (!value) return null
		return `.${name}{width:${value};height:${value}}`
	}

	for (const [prefix, prop] of Object.entries(positionProps)) {
		const match = name.match(new RegExp(`^(-)?${prefix}-(\\d+|full|auto)$`))
		if (match) {
			const value = parseSignedValue(match[1] === '-', match[2])
			if (!value) return null
			return `.${name}{${prop}:${value}}`
		}
	}

	const gridCols = name.match(/^grid-cols-(\d+)$/)
	if (gridCols)
		return `.${name}{grid-template-columns:repeat(${gridCols[1]},minmax(0,1fr))}`

	const rounded = name.match(/^rounded-(\d+|full)$/)
	if (rounded) {
		const value =
			rounded[1] === 'full'
				? '9999px'
				: `${Number.parseInt(rounded[1], 10) * 0.25}rem`
		return `.${name}{border-radius:${value}}`
	}

	const zIndex = name.match(/^z-(\d+)$/)
	if (zIndex) {
		return `.${name}{z-index:${zIndex[1]}}`
	}

	const negativeZIndex = name.match(/^-z-(\d+)$/)
	if (negativeZIndex) {
		return `.${name}{z-index:-${negativeZIndex[1]}}`
	}

	const opacity = name.match(/^opacity-(\d+)$/)
	if (opacity) {
		return `.${name}{opacity:${Number.parseInt(opacity[1], 10) / 100}}`
	}

	return null
}
